import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../i18n';
import { CONTACT_DISPLAY, whatsappLink } from '../contact';
import { LanguageSwitcher } from '../components/LanguageSwitcher';

const FEATURES = [
  { key: 'campaigns', tone: 'bg-emerald-50 text-emerald-700' },
  { key: 'inbox', tone: 'bg-sky-50 text-sky-700' },
  { key: 'analytics', tone: 'bg-indigo-50 text-indigo-700' },
  { key: 'contacts', tone: 'bg-amber-50 text-amber-700' },
  { key: 'optout', tone: 'bg-rose-50 text-rose-700' },
  { key: 'privacy', tone: 'bg-slate-100 text-slate-700' },
];

const FEATURE_ICONS: Record<string, JSX.Element> = {
  campaigns: <path d="M4 8h4l8-4v16l-8-4H4a2 2 0 01-2-2V10a2 2 0 012-2zm12 0a4 4 0 010 8" />,
  inbox: <path d="M3 12h4l2 3h6l2-3h4M5 5h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z" />,
  analytics: <path d="M4 20V10m6 10V4m6 16v-7" />,
  contacts: <path d="M12 7a3 3 0 110 6 3 3 0 010-6zM4 21v-1a5 5 0 015-5h6a5 5 0 015 5v1" />,
  optout: <path d="M12 3a9 9 0 110 18 9 9 0 010-18zM6 6l12 12" />,
  privacy: <path d="M12 3l7 3v6c0 4-3 7-7 9-4-2-7-5-7-9V6l7-3zm0 7v4" />,
};

const STEPS = ['one', 'two', 'three'];

const FAQ = ['1', '2', '3', '4', '5', '6'];

const PLANS = [
  { name: 'Starter', messages: '5,000', contacts: '5,000', numbers: '1', users: '3' },
  { name: 'Pro', messages: '25,000', contacts: '25,000', numbers: '3', users: '10', highlight: true },
  { name: 'Business', messages: '100,000', contacts: '100,000', numbers: '10', users: '25' },
];

// Números da maquete do herói: ilustram o relatório, com proporções coerentes entre si
const MOCK = [
  { key: 'sent', value: '2,480', bar: 100, tone: 'bg-slate-300' },
  { key: 'delivered', value: '2,361', bar: 95, tone: 'bg-emerald-500' },
  { key: 'read', value: '1,914', bar: 77, tone: 'bg-sky-500' },
  { key: 'replied', value: '312', bar: 13, tone: 'bg-indigo-500' },
];

function WhatsAppIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.5 14.4c-.3-.2-1.7-.9-2-1-.3-.1-.5-.2-.7.1-.2.3-.7 1-.9 1.2-.2.2-.3.2-.6.1-.3-.2-1.2-.5-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6l.5-.5c.1-.2.2-.3.3-.5 0-.2 0-.4 0-.5 0-.2-.7-1.6-.9-2.2-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.2.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.7-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.2-.3-.2-.6-.4z" />
      <path d="M12 2a10 10 0 00-8.6 15L2 22l5.2-1.4A10 10 0 1012 2zm0 18.2a8.2 8.2 0 01-4.2-1.2l-.3-.2-3.1.8.8-3-.2-.3A8.2 8.2 0 1112 20.2z" />
    </svg>
  );
}

function Section({ id, children, className = '' }: { id?: string; children: React.ReactNode; className?: string }) {
  return (
    <section id={id} className={`px-5 py-20 ${className}`}>
      <div className="max-w-6xl mx-auto">{children}</div>
    </section>
  );
}

function ProductPreview({ t }: { t: (key: string, vars?: Record<string, string | number>) => string }) {
  return (
    <div className="relative">
      <div className="absolute -inset-4 bg-gradient-to-tr from-emerald-200/40 via-sky-200/30 to-transparent blur-2xl rounded-[2rem]" />
      <div className="relative rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/5 overflow-hidden">
        {/* Barra da janela */}
        <div className="flex items-center gap-1.5 px-4 py-3 border-b border-slate-100 bg-slate-50">
          <span className="w-2.5 h-2.5 rounded-full bg-red-300" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-300" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-300" />
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-semibold text-slate-900 truncate">{t('landing.mock.campaign')}</p>
              <p className="text-xs text-emerald-600 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                {t('landing.mock.running')}
              </p>
            </div>
            <span className="text-xs text-slate-400">98%</span>
          </div>

          <div className="space-y-2.5">
            {MOCK.map(row => (
              <div key={row.key}>
                <div className="flex justify-between text-xs text-slate-500">
                  <span>{t(`landing.mock.${row.key}`)}</span>
                  <span className="font-semibold text-slate-800">{row.value}</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 mt-1 overflow-hidden" dir="ltr">
                  <div className={`h-full rounded-full ${row.tone}`} style={{ width: `${row.bar}%` }} />
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-slate-100 bg-slate-50 p-3 space-y-2">
            <p className="text-[11px] font-medium text-slate-500">{t('landing.mock.inbox')}</p>
            <div className="flex">
              <span className="bg-white border border-slate-200 rounded-2xl px-3 py-1.5 text-xs text-slate-700 max-w-[80%]">
                {t('landing.mock.msgIn')}
              </span>
            </div>
            <div className="flex justify-end">
              <span className="bg-emerald-600 text-white rounded-2xl px-3 py-1.5 text-xs max-w-[80%]">
                {t('landing.mock.msgOut')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// Números da maquete do relatório: coerentes entre si, com telefones mascarados
const REPORT_RATES = [
  { key: 'deliveryRate', value: '95%', width: 95, tone: 'bg-emerald-500', text: 'text-emerald-600' },
  { key: 'readRate', value: '77%', width: 77, tone: 'bg-sky-500', text: 'text-sky-600' },
  { key: 'replyRate', value: '12.6%', width: 13, tone: 'bg-indigo-500', text: 'text-indigo-600' },
];

const REPORT_FAILURES = [
  { key: 'fail1', count: 87, width: 100 },
  { key: 'fail2', count: 46, width: 53 },
];

const REPORT_REPLIES = [
  { phone: '+9715•• ••4412', key: 'reply1', minutes: 3 },
  { phone: '+9715•• ••7830', key: 'reply2', minutes: 11 },
  { phone: '+9715•• ••2096', key: 'reply3', minutes: 24 },
];

function ReportPreview({ t }: { t: (key: string, vars?: Record<string, string | number>) => string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/5 overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-4 border-b border-slate-100">
        <div>
          <p className="font-semibold text-slate-900">{t('landing.report.campaign')}</p>
          <p className="text-xs text-slate-500">{t('landing.report.progress')} · 2,480</p>
        </div>
        <span className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 text-slate-600">
          {t('landing.report.export')}
        </span>
      </div>

      <div className="p-5 space-y-5">
        <div className="grid grid-cols-3 gap-3">
          {REPORT_RATES.map(rate => (
            <div key={rate.key} className="rounded-xl border border-slate-100 bg-slate-50/70 p-3">
              <p className="text-[11px] text-slate-500">{t(`landing.report.${rate.key}`)}</p>
              <p className={`text-xl font-bold mt-0.5 ${rate.text}`}>{rate.value}</p>
              <div className="h-1.5 bg-slate-200 rounded-full mt-2 overflow-hidden" dir="ltr">
                <div className={`h-full rounded-full ${rate.tone}`} style={{ width: `${rate.width}%` }} />
              </div>
            </div>
          ))}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-slate-100 p-4">
            <p className="text-xs font-medium text-slate-500 mb-3">{t('landing.report.failures')}</p>
            <div className="space-y-2.5">
              {REPORT_FAILURES.map(failure => (
                <div key={failure.key}>
                  <div className="flex justify-between gap-2 text-xs">
                    <span className="text-slate-600 truncate">{t(`landing.report.${failure.key}`)}</span>
                    <span className="font-semibold text-slate-800">{failure.count}</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full mt-1 overflow-hidden" dir="ltr">
                    <div className="h-full bg-orange-400 rounded-full" style={{ width: `${failure.width}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-slate-100 p-4">
            <p className="text-xs font-medium text-slate-500 mb-3">{t('landing.report.repliers')}</p>
            <div className="space-y-2.5">
              {REPORT_REPLIES.map(reply => (
                <div key={reply.phone} className="flex items-center gap-2.5">
                  <span className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 text-[11px] flex items-center justify-center shrink-0">
                    ✓✓
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-slate-800" dir="ltr">{reply.phone}</p>
                    <p className="text-[11px] text-slate-500 truncate">
                      {t(`landing.report.${reply.key}`)} · {t('landing.report.minutes', { count: reply.minutes })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function LandingPage() {
  const t = useT();
  const [brand, setBrand] = useState('Arkan Marketing');
  const [signupEnabled, setSignupEnabled] = useState(false);
  const [trialDays, setTrialDays] = useState(14);
  const [openQuestion, setOpenQuestion] = useState<string | null>('1');

  useEffect(() => {
    fetch('/api/settings/public')
      .then(response => response.json())
      .then(data => { if (data?.companyName) setBrand(data.companyName); })
      .catch(() => undefined);

    fetch('/api/auth/signup/status')
      .then(response => response.json())
      .then(data => { setSignupEnabled(!!data.enabled); setTrialDays(data.trialDays || 14); })
      .catch(() => undefined);
  }, []);

  const contactHref = whatsappLink(t('landing.contact.prefill'));
  const startHref = signupEnabled ? '/signup' : '/login';
  const startLabel = signupEnabled ? t('landing.ctaTrial', { days: trialDays }) : t('common.signIn');

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="sticky top-0 z-20 bg-white/85 backdrop-blur border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between gap-4">
          <span className="font-bold text-lg truncate" dir="auto">{brand}</span>
          <nav className="flex items-center gap-5 text-sm">
            <a href="#features" className="hidden md:inline text-slate-600 hover:text-slate-900">{t('landing.menu.features')}</a>
            <a href="#how" className="hidden md:inline text-slate-600 hover:text-slate-900">{t('landing.menu.how')}</a>
            <a href="#plans" className="hidden md:inline text-slate-600 hover:text-slate-900">{t('landing.menu.plans')}</a>
            <a href="#faq" className="hidden md:inline text-slate-600 hover:text-slate-900">{t('landing.menu.faq')}</a>
            <LanguageSwitcher className="text-slate-600 hover:text-slate-900 hover:bg-slate-100" />
            <a
              href={contactHref}
              target="_blank"
              rel="noreferrer"
              className="hidden lg:flex items-center gap-1.5 text-slate-600 hover:text-emerald-700"
              title={t('landing.contact.whatsapp')}
            >
              <WhatsAppIcon />
              <span dir="ltr">{CONTACT_DISPLAY}</span>
            </a>
            <Link to="/login" className="text-slate-700 hover:text-slate-900 font-medium">{t('common.signIn')}</Link>
            {signupEnabled && (
              <Link to="/signup" className="px-4 py-2 rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 whitespace-nowrap">
                {t('common.getStarted')}
              </Link>
            )}
          </nav>
        </div>
      </header>

      {/* Herói: promessa à esquerda, produto à direita */}
      <section className="relative overflow-hidden px-5 pt-16 pb-20 bg-gradient-to-b from-emerald-50/70 via-white to-white">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-block text-xs font-semibold tracking-wide uppercase text-emerald-700 bg-emerald-100 px-3 py-1 rounded-full">
              {t('landing.badge')}
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl font-bold leading-[1.15]">
              {t('landing.title')}<br />
              <span className="text-emerald-600">{t('landing.titleAccent')}</span>
            </h1>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">{t('landing.subtitle')}</p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={startHref} className="px-6 py-3 rounded-xl bg-emerald-600 text-white font-medium hover:bg-emerald-700 shadow-lg shadow-emerald-600/20">
                {startLabel}
              </Link>
              <a href="#how" className="px-6 py-3 rounded-xl border border-slate-200 font-medium hover:bg-slate-50">
                {t('landing.ctaDemo')}
              </a>
            </div>

            <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-500">
              {['note1', 'note2', 'note3'].map(note => (
                <li key={note} className="flex items-center gap-1.5">
                  <span className="text-emerald-600">✓</span>{t(`landing.${note}`)}
                </li>
              ))}
            </ul>
          </div>

          <ProductPreview t={t} />
        </div>
      </section>

      {/* Três pilares */}
      <Section className="border-y border-slate-100 bg-slate-50/60">
        <div className="grid gap-8 md:grid-cols-3">
          {['send', 'reply', 'measure'].map((pillar, index) => (
            <div key={pillar} className="flex gap-4">
              <span className="shrink-0 w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center font-bold text-emerald-600">
                {index + 1}
              </span>
              <div>
                <h3 className="font-semibold text-lg">{t(`landing.pillars.${pillar}.title`)}</h3>
                <p className="mt-1 text-slate-600 text-sm leading-relaxed">{t(`landing.pillars.${pillar}.text`)}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Relatório: a prova de que dá para medir */}
      <Section>
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs font-semibold tracking-wide uppercase text-emerald-700">{t('landing.report.eyebrow')}</span>
            <h2 className="mt-3 text-3xl font-bold leading-snug">{t('landing.report.title')}</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">{t('landing.report.text')}</p>
            <ul className="mt-6 space-y-3 text-slate-700">
              {['point1', 'point2', 'point3'].map(point => (
                <li key={point} className="flex gap-3">
                  <span className="shrink-0 w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-sm flex items-center justify-center">✓</span>
                  <span>{t(`landing.report.${point}`)}</span>
                </li>
              ))}
            </ul>
          </div>

          <ReportPreview t={t} />
        </div>
      </Section>

      {/* Recursos */}
      <Section id="features" className="bg-slate-50">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold">{t('landing.features.title')}</h2>
          <p className="mt-3 text-slate-600">{t('landing.features.subtitle')}</p>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(feature => (
            <div key={feature.key} className="rounded-2xl border border-slate-200 p-6 hover:border-emerald-200 hover:shadow-md transition">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${feature.tone}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  {FEATURE_ICONS[feature.key]}
                </svg>
              </div>
              <h3 className="mt-4 font-semibold text-lg">{t(`landing.features.${feature.key}.title`)}</h3>
              <p className="mt-2 text-slate-600 text-sm leading-relaxed">{t(`landing.features.${feature.key}.text`)}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Passos */}
      <Section id="how">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold">{t('landing.steps.title')}</h2>
          <p className="mt-3 text-slate-600">{t('landing.steps.subtitle')}</p>
        </div>
        <div className="mt-12 grid gap-8 md:grid-cols-3">
          {STEPS.map((step, index) => (
            <div key={step} className="relative rounded-2xl bg-white border border-slate-200 p-6">
              <span className="absolute -top-4 start-6 w-9 h-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-bold shadow-lg shadow-emerald-600/25">
                {index + 1}
              </span>
              <h3 className="mt-3 font-semibold text-lg">{t(`landing.steps.${step}.title`)}</h3>
              <p className="mt-2 text-slate-600 text-sm leading-relaxed">{t(`landing.steps.${step}.text`)}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Ritmo de envio */}
      <Section>
        <div className="rounded-3xl bg-slate-900 text-white p-10 md:p-14">
          <div className="max-w-3xl">
            <h2 className="text-2xl md:text-3xl font-bold">{t('landing.policy.title')}</h2>
            <p className="mt-4 text-slate-300 leading-relaxed">{t('landing.policy.text')}</p>
            <div className="mt-7 flex flex-wrap gap-2">
              {['chip1', 'chip2', 'chip3', 'chip4'].map(chip => (
                <span key={chip} className="text-sm bg-white/10 border border-white/15 rounded-full px-4 py-1.5">
                  {t(`landing.policy.${chip}`)}
                </span>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Planos */}
      <Section id="plans" className="bg-slate-50">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold">{t('landing.plans.title')}</h2>
          <p className="mt-3 text-slate-600">{t('landing.plans.subtitle', { days: trialDays })}</p>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-3 items-start">
          {PLANS.map(plan => (
            <div
              key={plan.name}
              className={`rounded-2xl bg-white p-6 ${plan.highlight
                ? 'border-2 border-emerald-500 shadow-xl shadow-emerald-600/10 md:-mt-3'
                : 'border border-slate-200'}`}
            >
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-xl font-bold">{plan.name}</h3>
                {plan.highlight && (
                  <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full whitespace-nowrap">
                    {t('landing.plans.popular')}
                  </span>
                )}
              </div>
              <ul className="mt-5 space-y-2.5 text-sm text-slate-600">
                {[
                  t('landing.plans.messages', { count: plan.messages }),
                  t('landing.plans.contacts', { count: plan.contacts }),
                  t('landing.plans.numbers', { count: plan.numbers }),
                  t('landing.plans.users', { count: plan.users }),
                  t('landing.plans.included'),
                ].map(line => (
                  <li key={line} className="flex gap-2">
                    <span className="text-emerald-600 shrink-0">✓</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={startHref}
                className={`mt-6 block text-center px-4 py-2.5 rounded-lg font-medium ${plan.highlight
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                  : 'border border-slate-200 hover:bg-slate-50'}`}
              >
                {signupEnabled ? t('landing.plans.startFree') : t('common.signIn')}
              </Link>
            </div>
          ))}
        </div>

        <p className="mt-8 text-center text-sm text-slate-500">{t('landing.plans.note')}</p>
      </Section>

      {/* Perguntas */}
      <Section id="faq">
        <h2 className="text-3xl font-bold text-center">{t('landing.faq.title')}</h2>
        <div className="mt-10 max-w-3xl mx-auto divide-y divide-slate-200 border-y border-slate-200">
          {FAQ.map(item => {
            const open = openQuestion === item;
            return (
              <div key={item}>
                <button
                  onClick={() => setOpenQuestion(open ? null : item)}
                  className="w-full text-start py-5 flex items-center justify-between gap-4 hover:text-emerald-700"
                >
                  <span className="font-medium">{t(`landing.faq.q${item}`)}</span>
                  <span className={`shrink-0 text-slate-400 transition-transform ${open ? 'rotate-45' : ''}`}>+</span>
                </button>
                {open && <p className="pb-5 -mt-1 text-slate-600 text-sm leading-relaxed">{t(`landing.faq.a${item}`)}</p>}
              </div>
            );
          })}
        </div>
      </Section>

      {/* Chamada final */}
      <Section className="pt-0">
        <div className="rounded-3xl bg-gradient-to-br from-emerald-600 to-emerald-700 text-white p-10 md:p-14 text-center">
          <h2 className="text-2xl md:text-3xl font-bold">{t('landing.cta.title')}</h2>
          <p className="mt-3 text-emerald-50">{t('landing.cta.text')}</p>
          <div className="mt-7 flex flex-wrap gap-3 justify-center">
            <Link to={startHref} className="px-7 py-3 rounded-xl bg-white text-emerald-700 font-semibold hover:bg-emerald-50">
              {startLabel}
            </Link>
            <a
              href={contactHref}
              target="_blank"
              rel="noreferrer"
              className="px-7 py-3 rounded-xl border border-white/40 text-white font-semibold hover:bg-white/10 flex items-center gap-2"
            >
              <WhatsAppIcon className="w-5 h-5" />
              {t('landing.contact.whatsapp')}
            </a>
          </div>
        </div>
      </Section>

      <footer className="px-5 py-10 border-t border-slate-100">
        <div className="max-w-6xl mx-auto flex flex-wrap gap-4 items-center justify-between text-sm text-slate-500">
          <div>
            <p className="font-semibold text-slate-700" dir="auto">{brand}</p>
            <p className="mt-1">{t('landing.footer.tagline')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-5">
            <a href={contactHref} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-emerald-700 hover:text-emerald-900">
              <WhatsAppIcon />
              <span>
                {t('landing.contact.footer')}: <span dir="ltr" className="font-medium">{CONTACT_DISPLAY}</span>
              </span>
            </a>
            <Link to="/login" className="hover:text-slate-800">{t('common.signIn')}</Link>
            {signupEnabled && <Link to="/signup" className="hover:text-slate-800">{t('common.createAccount')}</Link>}
          </div>
        </div>
        <p className="max-w-6xl mx-auto mt-6 text-xs text-slate-400">© {new Date().getFullYear()} {brand}</p>
      </footer>
    </div>
  );
}

export default LandingPage;
